"use client";

import dynamic from "next/dynamic";
import { useRouter } from "next/navigation";
import React, { Key, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ApiError } from "@/app/common-types/api-error.types";
import {
  Dialog,
  Dropdown,
  DropdownItem,
  Modal,
  XIcon,
} from "staysure-component-library";

import BtnLoadingSvg from "@/components/atoms/btn-loading-svg/btn-loading-svg";
import LabelButtonWrapper, {
  IconButtonWrapper,
} from "@/components/atoms/button-wrapper/button-wrapper";
import RichText from "@/components/atoms/rich-text/rich-text";
import withApplication from "@/components/with-application";
import withBrand from "@/components/with-brand";
import { RenewalPage } from "@/config/app-constant";
import { BRAND_STAYSURE } from "@/constants";
import useUpdateCancellationCover from "@/hooks/api/quote/use-update-cancellation-cover";
import { useWizardTranslation } from "@/libs/i18n/use-wizard-translation";
import { CancellationCovers } from "@/schema/quote-page/page.schema";
import { RootState } from "@/store/root-reducer";
import { setApiError } from "@/store/slices/errors/api-error.slice";
import { updateQuote } from "@/store/slices/quote/quote.slice";
import { QuoteData } from "@/types/quote/quote-data";
import { apiErrorMappings } from "@/utils/errors/api-error-map";
import handleError, { ErrorResponse } from "@/utils/handle-errors";

import styles from "./cancellation-modal.module.css";

const APIErrorModal = dynamic(
  () => import("@/components/molecules/api-error-modal/api-error-modal")
);

type RequestdataProps = {
  requestdata: {
    policyPackage?: string;
    policyType?: string;
    selectedCancellationCover?: SelectedCover[];
  };
};

type CancellationCoverModalProps = {
  cancelationCoverCancelCta?: string;
  cancelationCoverDescription?: string;
  cancelationCoverTitle?: string;
  cancelationCoverUpdateCta?: string;
  isOpen: boolean;
  packageType: string;
  tripType: string;
  values: CancellationCovers[] | null;
  setIsOpen: (val: boolean) => void;
  placeholderText?: string;
  brand?: string;
};

type SelectedCover = { selected: boolean; value: number };

function CancellationCoverModal({
  cancelationCoverCancelCta,
  cancelationCoverDescription = "",
  cancelationCoverTitle = "Cancellation Cover",
  cancelationCoverUpdateCta,
  isOpen,
  packageType,
  tripType,
  values,
  setIsOpen,
  placeholderText = "Select cover amount",
  application,
  brand = "staysure",
}: Readonly<CancellationCoverModalProps & { application?: string }>) {
  const dispatch = useDispatch();
  const router = useRouter();
  const { t } = useWizardTranslation();
  // const [accesibilityClassName, setAccesibilityClassName] = useState<boolean>();
  const [cancelCover, setCancelCover] = useState<SelectedCover[]>([]);
  const [updateCover, setUpdateCover] = useState(false);
  const { attributes } = useSelector((state: RootState) => state.quote);
  const { tripDetails } = attributes;
  const activePolicyPackage = tripDetails?.policyPackage;

  function setPatchBody(postData: RequestdataProps["requestdata"]) {
    return {
      dynamicCoverLimit: [
        {
          code: "CANCELLATION",
          product: [
            {
              product: "CORE",
              policyType: postData.policyType,
              policyPackage: postData.policyPackage,
              values: postData.selectedCancellationCover,
            },
          ],
        },
      ],
    };
  }

  function setTripDetails(
    attribs: QuoteData["attributes"],
    postData: RequestdataProps["requestdata"]
  ) {
    const data = { ...attribs.tripDetails };

    data.policyPackage = activePolicyPackage?.toUpperCase() ?? "";
    data.policyType = postData.policyType ?? "";

    return data;
  }

  const { data, loading, error, showErrorModal, updateCancellationCover } =
    useUpdateCancellationCover({
      resetTrigger: useCallback(() => setUpdateCover(false), []),
    });

  useEffect(() => {
    const handleEffect = async () => {
      if (showErrorModal && brand === BRAND_STAYSURE) {
        setIsOpen(true);
        return;
      }

      if (!loading && data && !error) {
        dispatch(updateQuote(data));
        setIsOpen(false);
      } else if (!loading && error !== null) {
        if (application === RenewalPage) {
          await handleError(error as ApiError<ErrorResponse>, router, dispatch);
        } else if (error.response?.status === 401) {
          router.push("/");
        } else {
          const errorObject = await apiErrorMappings(error);
          errorObject.multiButton = true;
          errorObject.validationEnable = true;
          dispatch(setApiError(errorObject));
          setIsOpen(false);
        }
      }
    };
    handleEffect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, loading, error, dispatch, setIsOpen, router]);

  const updateCancellationCoverHandler = () => {
    let selectedCancellationCover: SelectedCover[];

    if (cancelCover && cancelCover.length === 0 && values) {
      selectedCancellationCover = values.map((obj) => ({
        selected: obj.selected === true,
        value: obj.value,
      }));
    } else {
      selectedCancellationCover = cancelCover;
    }
    const requestdata = {
      policyType: tripType,
      policyPackage: packageType.toUpperCase(),
      selectedCancellationCover,
    };
    const body = setPatchBody(requestdata);
    const tripDetailsBody = setTripDetails(attributes, requestdata);

    updateCancellationCover(body, tripDetailsBody);
    setUpdateCover(true);
  };

  const setCancellationCover = (value: string) => {
    const selectedCover: SelectedCover[] = [];
    values?.forEach((cover) => {
      let selected = false;
      if (cover.value.toString() === value) {
        selected = true;
      }
      selectedCover.push({
        selected,
        value: cover.value,
      });
    });
    setCancelCover(selectedCover);
  };

  const selectedCover = values?.find((cover) => {
    if (cover.selected) {
      return cover.displayValue;
    }
    return "";
  });

  if (showErrorModal) {
    return (
      <APIErrorModal
        title={t("api_error_title", "Something went wrong")}
        description={t(
          "api_error_cancellation_description",
          "We're sorry, we can't update or retrieve your quote. Please start a new quote to continue."
        )}
        isOpenModal={isOpen}
        isCtaPrimaryBtnEnabled={false}
        isCloseIconEnabled={false}
      />
    );
  }

  return (
    <Modal
      onOpenChange={(e: boolean) => setIsOpen(e)}
      isOpen={isOpen}
      modalStyles={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      }}
      className="bg-surface-neutral-option-1 md:w-[416px] sm:w-[327px] relative rounded-radius-lg "
      uderlayClassName="z-[100] bg-interactive-overlay-default w-[100%] !fixed !top-0 !left-0 h-[100vh]"
    >
      <Dialog
        className=" relative"
        data-testid="cancellation-cover-modal-test-id"
      >
        <div className="md:p-spacing-xxxl p-spacing-l">
          <div className="md:w-spacing-xxxl md:h-spacing-xxxl w-spacing-xxl h-spacing-xxl absolute m-spacing-xxs right-0 top-0 flex items-center justify-center">
            <IconButtonWrapper
              data-testid="overlay-close-button-test-id"
              id="cancellation-cover-close-button"
              hierarchy="tertiary"
              btnType="icon"
              icon={<XIcon stroke="#306582" />}
              className="w-[24px] h-[24px] text-[24px] text-text-on-primary "
              onClick={() => {
                if (setIsOpen !== undefined) {
                  setIsOpen(false);
                }
              }}
            />
          </div>
          <header
            className="mb-spacing-xs"
            data-testid="helpOverLay-overlay-content-test-id"
          >
            <h1 className=" font-header-font text-[23px] md:text-3xl leading-[27.6px] md:leading-[34.1px] text-left text-text-title mb-spacing-s">
              {cancelationCoverTitle}
            </h1>
          </header>
          <main className="w-full">
            <div className="mb-spacing-l">
              <div className="text-text-functional text-xs text-left">
                <RichText richText={cancelationCoverDescription} />
              </div>
            </div>
            <div className="flex mt-spacing-s gap-spacing-xs md:gap-spacing-s mb-space-l">
              <div
                className={` ${styles.cancellation_cover_wrapper} border hover:border-2 !border-interactive-secondary-default w-[100%] !h-[56px]`}
                data-testid="cancellation-cover-dropdown"
              >
                <Dropdown
                  id={`cancellation-cover-dropdown-${packageType}`}
                  // onFocusChange={(e) => setAccesibilityClassName(e)}
                  placeholderText={placeholderText}
                  className={`${styles.droplistBox} py-spacing-m py px-spacing-m 
                flex space-between w-full`}
                  // placeholderTextStyle={{
                  //   margin: "0px 0px 0px 0px",
                  // }}
                  // unorderedListClassName="unorder-list"
                  listBoxStyle={{
                    outline: "none",
                    margin: "10px 0px 0px 0px",
                    minWidth: "unset !important",
                    boxShadow: "0px 0px 12px 0px rgba(15, 23, 42, 0.11)",
                  }}
                  onSelectionChange={(e: Key | null) => {
                    if (e) {
                      setCancellationCover(e as string);
                    }
                  }}
                  unorderedListClassName="sm:w-[244px] tablet:w-[280px] lg:w-[304px]"
                  onChangeCapture={() => {}}
                  defaultValue={`${selectedCover?.value ?? placeholderText}`}
                  // dropdownBtnStyle={accesibilityClassName ? {} : {}}
                >
                  {values ? (
                    values.map((cover, index) => (
                      <DropdownItem
                        value={`${cover.value}`}
                        key={`drop_down_item${index as unknown as string}`}
                        keyValue={`${cover.value}`}
                      >
                        <span
                          className=" w-full leading-[24px] Proxima Nova text-2xs font-semibold p-spacing-xs 
                    hover:bg-interactive-action-hovered country-name focus:bg-interactive-action-hovered text-text-on-primary"
                        >
                          {cover.displayValue}
                        </span>
                      </DropdownItem>
                    ))
                  ) : (
                    /* istanbul ignore next 
                      this is display in case of an api error
                    */
                    <DropdownItem value="empty" key="empty">
                      <span
                        className=" w-full leading-[24px] Proxima Nova text-2xs font-semibold p-spacing-xs 
                      hover:bg-interactive-action-hovered country-name focus:bg-interactive-action-hovered text-text-on-primary"
                      >
                        No cover
                      </span>
                    </DropdownItem>
                  )}
                </Dropdown>
              </div>
            </div>
            <div className="flex flex-col md:flex-row pt-spacing-l gap-spacing-xs text-text-functional">
              <LabelButtonWrapper
                id="cancelation-cover-update-button"
                hierarchy="primary"
                data-testid="update-button-test-id"
                label={cancelationCoverUpdateCta ?? "Update"}
                className={` ${styles.modal_button} md:max-w-[148px] text-text-functional`}
                //   className={`${Styles.textSelectionButton} ${className} !rounded-[0px]`}
                onClick={() => updateCancellationCoverHandler()}
                isLoading={updateCover}
                loadingIcon={<BtnLoadingSvg />}
              />
              <LabelButtonWrapper
                id="cancelation-cover-cancel-button"
                hierarchy="secondary"
                data-testid="cancel-button-test-id"
                label={cancelationCoverCancelCta ?? "Cancel"}
                className={` ${styles.modal_button} md:max-w-[148px]`}
                onClick={() => setIsOpen(false)}
              />
            </div>
          </main>
        </div>
      </Dialog>
    </Modal>
  );
}

export default withBrand(withApplication(CancellationCoverModal));
